// Background blur for the camera. A person segmenter (MediaPipe's selfie
// model, served from /mediapipe so a call never waits on a third party) marks
// which pixels are the person; each frame is drawn onto a canvas with the
// background blurred and the person sharp, and the canvas's own track is what
// the call publishes in place of the camera's. The raw camera track stays
// open underneath it and is what turning blur off goes back to.
//
// The mask has to move with the person exactly (Adam, 2026-10-04: "blur looks
// like it's always playing catch up to movement"). So each camera frame is
// drawn the moment it arrives (requestVideoFrameCallback, not a timer that can
// sit up to a frame behind), the canvas track sends each drawn frame at once,
// and the mask is the current frame's own while the person moves; a still person's edge is steadied against flicker (maskSettle) and never lags a move.
//
// The background is blurred from background pixels only (Adam, 2026-10-04, of
// a sharp line beside his neck: "there's this vertical line here that isn't
// right"). Blurring the whole frame smears the person into a glow around
// them, and an edge set wide to hide that glow kept a strip of real, sharp
// background inside the cutout. So the person is cut out first and the rest
// is blurred: a canvas blur works on premultiplied color, so each blurred
// pixel is the average of the background around it alone, and its alpha says
// how much background was there. Drawing that layer a few times over a coarse
// fill brings the alpha up to solid without changing the color.
//
// A wide frame can be shown to the segmenter as two overlapping squares
// (packages/core/src/maskTiles.ts, the `tiles` tuning), but it is off: on
// Adam's real camera the square that cut through his face read the cheek low
// and the join blurred a band across it, and the raised arm stayed blurred
// anyway (2026-10-14: "if anything, it's worse").
//
// A raised arm, a hand, and hair are kept by a second model, MediaPipe's
// multiclass selfie segmenter, which splits a person into hair, face skin,
// body skin, and clothes. Its hair and skin are a second way into the mask, so
// an arm close to the camera with no face in view stays sharp while the chair,
// which it reads only as clothes, keeps the high cutoff
// (packages/core/src/maskParts.ts). It runs in a worker (lib/partsWorker.ts)
// a few times a second, after the blur has started, so its run never stalls
// the picture; in the page where a worker cannot run it; and the blur runs
// without it if it fails or is slow.

import { ease, PART_BODY, PART_FACE, PART_HAIR, partsAlpha, partsField, planTiles, settleMask, tileJoiner, type TileJoin, type TileRect } from "@breeze/core";
import type { ImageSegmenter } from "@mediapipe/tasks-vision";
import { keepPlaying } from "./keepPlaying";
import type { PartsReply, PartsRequest } from "./partsWorker";

export interface BlurHandle {
  /** What to publish: the camera with its background blurred. */
  readonly track: MediaStreamTrack;
  /** The raw camera track this reads from. */
  readonly source: MediaStreamTrack;
  /** Reads from a different camera (a flip) without changing `track`. */
  setSource(track: MediaStreamTrack): void;
  /** Stops drawing and closes `track`; leaves `source` open. */
  stop(): void;
}

const BASE = "/mediapipe";
/** How often to draw when the browser has no requestVideoFrameCallback. */
const FALLBACK_FPS = 30;
/** The side of the square each part of a wide frame is drawn at for the
 * segmenter, which is the size the selfie model reads. */
const TILE_SIDE = 256;
/** Two squares cost two segmenter runs a frame. Where those average more than
 * this many milliseconds (a slow CPU), the frame goes back to being sent whole
 * so the call keeps its frame rate. */
const TILE_BUDGET_MS = 30;
/** The short side the frame is shrunk to for the segmenters. MediaPipe hands
 * a mask back at the size it was given, and both models read a 256 pixel
 * square, so a full 1280x720 frame cost a 921,600 pixel mask to copy and walk
 * every frame for no more detail than this gives (2026-10-18: "W bg blur it's
 * noticeably more laggy"). */
const SEG_SHORT = 256;

let segmenterPromise: Promise<ImageSegmenter> | null = null;
let partsPromise: Promise<ImageSegmenter | null> | null = null;

/** What a worker run hands back: the parts model's hair and skin field, or
 * the selfie model's person mask. */
interface WorkerResult {
  readonly field: Float32Array | null;
  readonly mask: { readonly data: Float32Array; readonly width: number; readonly height: number } | null;
  readonly ms: number;
}
const NO_RESULT: WorkerResult = { field: null, mask: null, ms: 0 };

/** A segmenter in its worker: one frame in, its answer out. */
interface PartsWorker {
  readonly delegate: string;
  run(bitmap: ImageBitmap, stamp: number): Promise<WorkerResult>;
}
const workerPromises: Record<"selfie" | "parts", Promise<PartsWorker | null> | null> = { selfie: null, parts: null };

const loadPartsWorker = (): Promise<PartsWorker | null> => loadWorker("parts");

/** The one worker of each kind every blur shares, or null where a worker
 * cannot run the model (no module workers, no ImageBitmap, or the model failed
 * there), which sends the blur back to running it in the page. */
function loadWorker(kind: "selfie" | "parts"): Promise<PartsWorker | null> {
  let workerPromise = workerPromises[kind];
  if (!workerPromise) {
    workerPromise = workerPromises[kind] = new Promise((resolve) => {
      if (typeof Worker === "undefined" || typeof createImageBitmap !== "function") {
        resolve(null);
        return;
      }
      let worker: Worker;
      try {
        worker = new Worker(new URL("./partsWorker.ts", import.meta.url), { type: "module" });
      } catch {
        resolve(null);
        return;
      }
      const waiting = new Map<number, (r: WorkerResult) => void>();
      let next = 1;
      let dead = false;
      const send = (msg: PartsRequest, transfer: Transferable[] = []): void => worker.postMessage(msg, transfer);
      const die = (why: string): void => {
        if (dead) return;
        dead = true;
        console.error(`[blur] ${kind} worker stopped: ${why}`);
        worker.terminate();
        for (const reply of waiting.values()) reply(NO_RESULT);
        waiting.clear();
        resolve(null);
      };
      worker.onmessage = (e: MessageEvent<PartsReply & { id?: number }>) => {
        const msg = e.data;
        if (msg.type === "ready") {
          console.info(`[blur] ${kind} model ready on the ${msg.delegate}, in a worker`);
          resolve({
            delegate: msg.delegate,
            run(bitmap, stamp) {
              if (dead) {
                bitmap.close();
                return Promise.resolve(NO_RESULT);
              }
              const id = next++;
              return new Promise((reply) => {
                waiting.set(id, reply);
                send({ type: "frame", id, bitmap, stamp }, [bitmap]);
              });
            },
          });
        } else if (msg.type === "failed") {
          die(msg.why);
        } else if (msg.type === "field") {
          waiting.get(msg.id)?.({ field: msg.field, mask: null, ms: msg.ms });
          waiting.delete(msg.id);
        } else if (msg.type === "mask") {
          waiting.get(msg.id)?.({ field: null, mask: msg.data ? { data: msg.data, width: msg.width, height: msg.height } : null, ms: msg.ms });
          waiting.delete(msg.id);
        }
      };
      worker.onerror = (e) => {
        e.preventDefault();
        die(e.message || "error");
      };
      send({ type: "init", base: new URL(BASE, location.href).href, kind });
    });
  }
  return workerPromise;
}

/** The one multiclass segmenter every blur shares, GPU first and then the
 * CPU. It only adds hair, hands, and arms, so a failure leaves the blur as it
 * was: null, kept, and not tried again this page. */
function loadParts(): Promise<ImageSegmenter | null> {
  if (!partsPromise) {
    partsPromise = (async () => {
      const { FilesetResolver, ImageSegmenter } = await import("@mediapipe/tasks-vision");
      for (const delegate of ["GPU", "CPU"] as const) {
        try {
          const fileset = await FilesetResolver.forVisionTasks(BASE);
          const made = await ImageSegmenter.createFromOptions(fileset, {
            baseOptions: { modelAssetPath: `${BASE}/selfie_multiclass_256x256.tflite`, delegate },
            runningMode: "VIDEO",
            outputConfidenceMasks: true,
            outputCategoryMask: false,
          });
          console.info(`[blur] parts model ready on the ${delegate}`);
          return made;
        } catch (err) {
          console.error(`[blur] ${delegate} parts segmenter failed`, err);
        }
      }
      return null;
    })();
  }
  return partsPromise;
}

/** Starts loading the segmenter before blur is turned on, so the first click
 * does not wait on it (Adam, 2026-10-14: "the first time i click blur bg
 * checkbox it's super unresponsive"). A failure here is forgotten and the
 * click tries again. With `parts`, for someone who keeps blur on, the parts
 * worker starts too and compiles its model before the picture needs it; it is
 * 16MB, so it is not fetched for someone who may never turn blur on. */
export function warmBlur(parts = false): void {
  if (!blurSupported()) return;
  void loadWorker("selfie").then((w) => {
    if (!w) void loadSegmenter().catch(() => undefined);
  });
  if (parts) void loadPartsWorker();
}

/** The selfie model on the page, only where its worker cannot run. A failed
 * load is forgotten so the next try starts again. CPU only: starting it on the
 * page's GPU held the page, and on Adam's laptop all of Chrome, for 9 to 12
 * seconds while its shaders compiled (QUEUE row 444). The real error is
 * logged so a failure on one machine says why. */
function loadSegmenter(): Promise<ImageSegmenter> {
  if (!segmenterPromise) {
    segmenterPromise = (async () => {
      const { FilesetResolver, ImageSegmenter } = await import("@mediapipe/tasks-vision");
      let last: unknown = null;
      for (const delegate of ["CPU", "CPU"] as const) {
        try {
          const fileset = await FilesetResolver.forVisionTasks(BASE);
          return await ImageSegmenter.createFromOptions(fileset, {
            baseOptions: { modelAssetPath: `${BASE}/selfie_segmenter.tflite`, delegate },
            runningMode: "VIDEO",
            outputConfidenceMasks: true,
            outputCategoryMask: false,
          });
        } catch (err) {
          last = err;
          console.error(`[blur] ${delegate} segmenter failed`, err);
          await new Promise((r) => setTimeout(r, 250));
        }
      }
      throw last instanceof Error ? last : new Error("segmenter failed to start");
    })().catch((err: unknown) => {
      segmenterPromise = null;
      throw err;
    });
  }
  return segmenterPromise;
}

/** Whether this browser can draw a blur at all. */
export function blurSupported(): boolean {
  return typeof document !== "undefined" && typeof HTMLCanvasElement.prototype.captureStream === "function";
}

// Where the person's edge sits, as a share of how sure the model is of the
// person in this frame. A fixed low cutoff (0.1 to 0.45) kept what the model
// was unsure of: Adam's black chair read 0.3 to 0.8 and the guitar behind him
// up to 0.7, and both stayed sharp as part of him (2026-10-04: "doesn't take
// my chair or the guitar behind me as part of my torso"), while his face and
// shirt read 1.0. A fixed high cutoff (0.25 to 0.6) had faded his face in a
// dimmer frame that morning ("now everything is out of focus"). Scaling the
// cutoff to the frame's own confidence in the person does both: in good
// light the edge sits high, and a frame where the whole person reads lower
// moves it down with them.
const EDGE_LOW = 0.62;
const EDGE_HIGH = 0.9;
// Where hair or skin from the multiclass model counts as person.
const PART_LOW = 0.25;
const PART_HIGH = 0.5;
/** The multiclass model runs once in this many milliseconds; its last field
 * stands in between. A run blocks the page for 60 to 70ms on Adam's laptop,
 * and one every fourth frame read as lag (2026-10-18: "W bg blur it's
 * noticeably more laggy"). */
const PARTS_EVERY_MS = 250;
/** In a worker a run stalls nothing, so it can run more often, one frame at a
 * time. */
const PARTS_WORKER_EVERY_MS = 120;
/** A worker's field is a frame behind by its run time; past this, averaged,
 * it no longer lines up with a moving arm and is dropped. */
const PARTS_WORKER_GIVE_UP_MS = 400;
/** The background is cut out and blurred at this share of the frame's size,
 * then stretched back. It is blurred anyway, so the smaller canvas looks the
 * same and costs a sixteenth of the pixels. */
const BG_SCALE = 0.25;
/** Past this many milliseconds a run, averaged, it stops for the call. On
 * Adam's laptop a run takes 60 to 70ms. */
const PARTS_GIVE_UP_MS = 100;
/** The first runs compile the model's GPU shaders and take seconds, so they
 * are left out of that average. Counting them dropped the model on every
 * machine before it had run a fair frame. */
const PARTS_WARMUP = 5;

/** How sure the model is of the person in this frame: the confidence the
 * surest tenth of the person-ish pixels reach. Floored so an empty frame
 * keeps a sensible edge. */
function personPeak(conf: Float32Array): number {
  const bins = new Uint32Array(64);
  let count = 0;
  for (let i = 0; i < conf.length; i++) {
    const c = conf[i]!;
    if (c < 0.3) continue;
    bins[Math.min(63, Math.floor(c * 64))]!++;
    count++;
  }
  if (count === 0) return 1;
  let seen = 0;
  for (let b = 63; b >= 0; b--) {
    seen += bins[b]!;
    if (seen >= count * 0.1) return Math.max(0.5, (b + 1) / 64);
  }
  return 1;
}

/** Draws of the background-only layer over the fill. Each one takes the
 * alpha a to 1 - (1 - a) * (1 - a) and leaves the color as it was. */
const BACKGROUND_LAYERS = 4;

/** Overrides for the blur lab (src/lab), so settings can be compared on one
 * frame. A call never passes these. */
export interface BlurTuning {
  readonly edgeLow?: number;
  readonly edgeHigh?: number;
  /** How the mask is steadied (packages/core/src/maskSettle.ts): "rise", the
   * default, takes a pixel becoming person at once; "both" eases either way;
   * "off" takes each frame's mask whole. */
  readonly settle?: "rise" | "both" | "off";
  /** Draws the mask itself instead of the blurred frame. */
  readonly showMask?: boolean;
  /** Whether a wide frame is segmented as two squares (packages/core
   * maskTiles.ts). Off by default. */
  readonly tiles?: boolean;
  /** Whether the multiclass model keeps hair, hands, and arms sharp
   * (packages/core/src/maskParts.ts). On by default. */
  readonly parts?: boolean;
  readonly partLow?: number;
  readonly partHigh?: number;
}

export async function createBlur(source: MediaStreamTrack, tune: BlurTuning = {}): Promise<BlurHandle> {
  const edgeLow = tune.edgeLow ?? EDGE_LOW;
  const edgeHigh = tune.edgeHigh ?? EDGE_HIGH;
  // `?blurmask` on the page's address shows the mask live over magenta, so a
  // blur that loses part of a real camera can be seen as the mask it drew.
  const showMask = tune.showMask ?? (typeof location !== "undefined" && new URLSearchParams(location.search).has("blurmask"));
  const partLow = tune.partLow ?? PART_LOW;
  const partHigh = tune.partHigh ?? PART_HIGH;
  // The parts worker loads and compiles while the selfie model starts; the
  // blur picks it up whenever it is ready.
  if (tune.parts ?? true) void loadPartsWorker();
  // The selfie model runs in its own worker on the CPU, so neither the page
  // nor the GPU stalls on it; the page runs it only where a worker cannot.
  const selfieWorker = await loadWorker("selfie");
  const segmenter = selfieWorker ? null : await loadSegmenter();
  // Loaded once the first frame is drawn and never awaited: the blur starts on
  // the selfie model alone and keeps hair and arms once this is ready.
  let parts: ImageSegmenter | null = null;
  let partsWanted = tune.parts ?? true;

  let raw = source;
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  // A phone call on the same phone (a declined Seamless ring, QUEUE row 430)
  // pauses this hidden video and mutes the camera, and nothing played it
  // again, so the blur kept handing out its last frame. keepPlaying plays it
  // after the interruption, and the camera's unmute plays it the moment the
  // picture is back.
  const replay = (): void => void video.play().catch(() => undefined);
  const attach = (track: MediaStreamTrack): void => {
    raw.removeEventListener("unmute", replay);
    track.addEventListener("unmute", replay);
    video.srcObject = new MediaStream([track]);
    replay();
  };
  attach(raw);
  const stopKeeping = keepPlaying(video);

  const out = document.createElement("canvas");
  const ctx = out.getContext("2d");
  if (!ctx) throw new Error("no canvas");
  const person = document.createElement("canvas");
  const personCtx = person.getContext("2d");
  const maskCanvas = document.createElement("canvas");
  const maskCtx = maskCanvas.getContext("2d");
  // The mask with its edge softened, at the mask's own size: feathering it
  // there costs a 256 pixel square instead of the whole frame.
  const featherCanvas = document.createElement("canvas");
  const featherCtx = featherCanvas.getContext("2d");
  const small = document.createElement("canvas");
  const smallCtx = small.getContext("2d");
  // The frame with the person cut out, and that blurred.
  const bg = document.createElement("canvas");
  const bgCtx = bg.getContext("2d");
  const bgBlur = document.createElement("canvas");
  const bgBlurCtx = bgBlur.getContext("2d");
  const bgSmall = document.createElement("canvas");
  const bgSmallCtx = bgSmall.getContext("2d");
  // The frame at the segmenters' size.
  const segIn = document.createElement("canvas");
  const segInCtx = segIn.getContext("2d");
  // One part of a wide frame, as the segmenter is shown it.
  const tile = document.createElement("canvas");
  tile.width = TILE_SIDE;
  tile.height = TILE_SIDE;
  const tileCtx = tile.getContext("2d");
  if (!personCtx || !maskCtx || !featherCtx || !segInCtx || !smallCtx || !bgCtx || !bgBlurCtx || !bgSmallCtx || !tileCtx) throw new Error("no canvas");
  const hasFilter: boolean = Reflect.has(ctx, "filter");

  // No frame rate: a frame goes out each time one is drawn, with no wait.
  const stream = out.captureStream();
  const track = stream.getVideoTracks()[0];
  if (!track) throw new Error("no output track");

  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastTime = -1;

  // The mask as shown, and the edge cutoff as shown: both steadied against
  // frame-to-frame wavering (packages/core/src/maskSettle.ts) so hair stops
  // flickering in and out of blur while a moving person still gets the new
  // mask in full.
  let settled: Float32Array | null = null;
  let peakSettled: number | null = null;

  // The segmenter wants rising timestamps, and a tiled frame calls it twice.
  let lastStamp = 0;
  const stamp = (): number => (lastStamp = Math.max(lastStamp + 0.01, performance.now()));
  const runSegmenter = (image: HTMLVideoElement | HTMLCanvasElement): { data: Float32Array; width: number; height: number } | null => {
    let got: { data: Float32Array; width: number; height: number } | null = null;
    if (!segmenter) return null;
    segmenter.segmentForVideo(image, stamp(), (result) => {
      const mask = result.confidenceMasks?.[0];
      if (!mask) return;
      got = { data: mask.getAsFloat32Array().slice(), width: mask.width, height: mask.height };
    });
    return got;
  };

  let tiling = tune.tiles ?? false;
  let tiles: TileRect[] = [];
  let joiner: TileJoin | null = null;
  let joinKey = "";
  let segMs: number | null = null;
  let segFrames = 0;
  let wholeFrames = 0;
  let wholeMs: number | null = null;
  // The latest person mask from the selfie worker. The picture is drawn with
  // the newest one that has come back, a run behind the frame, the way the
  // parts field already is.
  let latestMask: { data: Float32Array; width: number; height: number } | null = null;
  let selfieBusy = false;
  const askSelfie = (w: PartsWorker, sw: number, sh: number): void => {
    if (selfieBusy) return;
    selfieBusy = true;
    const started = performance.now();
    // A run that never answers frees the worker after a second.
    const timeout = setTimeout(() => (selfieBusy = false), 1000);
    void createImageBitmap(video, { resizeWidth: sw, resizeHeight: sh, resizeQuality: "medium" })
      .then((bitmap) => {
        if (stopped) {
          bitmap.close();
          return;
        }
        return w.run(bitmap, stamp()).then(({ mask }) => {
          if (stopped || !mask) return;
          latestMask = mask;
          wholeMs = ease(wholeMs, performance.now() - started, 0.1);
          if (++wholeFrames === 60) console.info(`[blur] selfie model: ${Math.round(wholeMs)}ms a run in its worker`);
        });
      })
      .catch(() => undefined)
      .finally(() => {
        clearTimeout(timeout);
        selfieBusy = false;
      });
  };
  /** The person confidence for this frame and its size: from the frame whole,
   * or from two squares joined (packages/core/src/maskTiles.ts). */
  const segment = (w: number, h: number): { data: Float32Array; width: number; height: number } | null => {
    const plan = tiling && !selfieWorker ? planTiles(w, h) : [];
    if (plan.length < 2) {
      const started = performance.now();
      const scale = Math.min(1, SEG_SHORT / Math.min(w, h));
      const sw = Math.max(1, Math.round(w * scale));
      const sh = Math.max(1, Math.round(h * scale));
      if (segIn.width !== sw || segIn.height !== sh) {
        segIn.width = sw;
        segIn.height = sh;
      }
      segInCtx.drawImage(video, 0, 0, sw, sh);
      if (selfieWorker) {
        askSelfie(selfieWorker, sw, sh);
        return latestMask && latestMask.width === sw && latestMask.height === sh ? latestMask : null;
      }
      const whole = runSegmenter(segIn);
      wholeMs = ease(wholeMs, performance.now() - started, 0.1);
      if (++wholeFrames === 60) console.info(`[blur] selfie model: ${Math.round(wholeMs)}ms a frame, whole`);
      return whole;
    }
    const started = performance.now();
    const parts = [];
    for (const r of plan) {
      tileCtx.drawImage(video, r.x, r.y, r.w, r.h, 0, 0, TILE_SIDE, TILE_SIDE);
      const part = runSegmenter(tile);
      if (!part) return null;
      parts.push(part);
    }
    const first = parts[0]!;
    const key = `${w}x${h}:${first.width}x${first.height}`;
    if (!joiner || key !== joinKey) {
      tiles = plan;
      joiner = tileJoiner(w, h, tiles, first.width, first.height);
      joinKey = key;
    }
    const data = joiner.join(parts);
    segMs = ease(segMs, performance.now() - started, 0.1);
    if (++segFrames === 60) console.info(`[blur] selfie model: ${Math.round(segMs)}ms a frame, two squares`);
    if (segFrames >= 30 && segMs > TILE_BUDGET_MS) {
      tiling = false;
      console.warn(`[blur] two squares took ${Math.round(segMs)}ms a frame; sending the frame whole`);
    }
    return { data, width: joiner.width, height: joiner.height };
  };
  // The latest hair and skin field, and what a run costs.
  let field: Float32Array | null = null;
  let partsMs: number | null = null;
  let partsRuns = 0;
  let partsAt = -Infinity;
  let worker: PartsWorker | null = null;
  let workerBusy = false;
  // What the ?blurmask view prints about the second model.
  let partsState = partsWanted ? "waiting" : "off";
  const dropParts = (why: string): void => {
    console.warn(`[blur] ${why}; hair and arms go back to the selfie model alone`);
    partsState = `dropped: ${why}`;
    parts = null;
    worker = null;
    field = null;
  };
  /** One frame to the worker when the last one is back; the field is used
   * from whenever it lands. */
  const askWorker = (w: PartsWorker): void => {
    const now = performance.now();
    if (workerBusy || now - partsAt < PARTS_WORKER_EVERY_MS || segIn.width === 0) return;
    partsAt = now;
    workerBusy = true;
    // A reply that never comes must not stop the asking.
    const timeout = setTimeout(() => (workerBusy = false), 2000);
    void createImageBitmap(video, { resizeWidth: segIn.width, resizeHeight: segIn.height, resizeQuality: "medium" })
      .then((bitmap) => {
        if (stopped || worker !== w) {
          bitmap.close();
          return;
        }
        return w.run(bitmap, stamp()).then(({ field: got, ms }) => {
          if (stopped || worker !== w) return;
          field = got;
          partsRuns++;
          if (partsRuns > PARTS_WARMUP) partsMs = ease(partsMs, ms, 0.1);
          if (partsRuns === 20 + PARTS_WARMUP) console.info(`[blur] parts model: ${Math.round(partsMs ?? 0)}ms a run in its worker, field ${got ? "in use" : "missing"}`);
          if (partsRuns >= 20 + PARTS_WARMUP && partsMs !== null && partsMs > PARTS_WORKER_GIVE_UP_MS) dropParts(`the parts model took ${Math.round(partsMs)}ms a run`);
        });
      })
      .catch(() => undefined)
      .finally(() => {
        clearTimeout(timeout);
        workerBusy = false;
      });
  };
  const readParts = (size: number): void => {
    if (partsWanted) {
      partsWanted = false;
      partsState = "loading";
      void loadPartsWorker().then(async (w) => {
        if (stopped) return;
        if (w) {
          worker = w;
          partsState = `running in a worker on the ${w.delegate}`;
          return;
        }
        const p = await loadParts();
        if (stopped) return;
        parts = p;
        partsState = p ? "running" : "failed to load";
      });
    }
    if (worker) {
      askWorker(worker);
      return;
    }
    if (!parts) return;
    const started = performance.now();
    if (started - partsAt < PARTS_EVERY_MS) return;
    partsAt = started;
    let ok = false;
    // The masks are read inside the callback, since they are closed when it
    // returns.
    parts.segmentForVideo(segIn, stamp(), (result) => {
      const got = result.confidenceMasks;
      if (!got) return;
      if (!field || field.length !== size) field = new Float32Array(size);
      // Only the three masks it uses. Reading a mask back off the GPU is most
      // of a run: all six took 131ms a run on Adam's laptop, these three 62ms.
      const used: Float32Array[] = [];
      for (const k of [PART_HAIR, PART_BODY, PART_FACE]) {
        const mask = got[k];
        if (mask) used[k] = mask.getAsFloat32Array();
      }
      ok = partsField(used, field);
    });
    if (!ok) field = null;
    partsRuns++;
    if (partsRuns > PARTS_WARMUP) partsMs = ease(partsMs, performance.now() - started, 0.1);
    if (partsRuns === 20 + PARTS_WARMUP) console.info(`[blur] parts model: ${Math.round(partsMs ?? 0)}ms a run, field ${ok ? "in use" : "missing"}`);
    if (partsRuns >= 20 + PARTS_WARMUP && partsMs !== null && partsMs > PARTS_GIVE_UP_MS) dropParts(`the parts model took ${Math.round(partsMs)}ms a run`);
  };
  const draw = (): void => {
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (w === 0 || h === 0 || video.readyState < 2 || video.currentTime === lastTime) return;
    lastTime = video.currentTime;
    if (out.width !== w || out.height !== h) {
      out.width = w;
      out.height = h;
      person.width = w;
      person.height = h;
      bg.width = Math.max(16, Math.round(w * BG_SCALE));
      bg.height = Math.max(9, Math.round(h * BG_SCALE));
      bgBlur.width = bg.width;
      bgBlur.height = bg.height;
    }
    const seg = segment(w, h);
    if (!seg) {
      if (selfieWorker) {
        // No mask back yet: the whole frame soft, so the room is never shown
        // sharp before the person is found.
        small.width = Math.max(16, Math.round(w / 32));
        small.height = Math.max(9, Math.round(h / 32));
        smallCtx.drawImage(video, 0, 0, small.width, small.height);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(small, 0, 0, w, h);
      } else ctx.drawImage(video, 0, 0, w, h);
      return;
    }
    const now = seg.data;
    const mw = seg.width;
    const mh = seg.height;
    if (maskCanvas.width !== mw || maskCanvas.height !== mh) {
      maskCanvas.width = mw;
      maskCanvas.height = mh;
      featherCanvas.width = mw;
      featherCanvas.height = mh;
    }
    const image = maskCtx.createImageData(mw, mh);
    const peak = (peakSettled = ease(peakSettled, personPeak(now), 0.3));
    const low = edgeLow * peak;
    const high = edgeHigh * peak;
    try {
      readParts(now.length);
    } catch (err) {
      console.error(err);
      dropParts("the parts model failed");
    }
    const alpha = new Float32Array(now.length);
    partsAlpha(now, field, low, high, partLow, partHigh, alpha);
    if (!settled || settled.length !== alpha.length) settled = alpha.slice();
    else if (tune.settle === "off") settled.set(alpha);
    else settleMask(settled, alpha, tune.settle ?? "rise");
    for (let i = 0; i < settled.length; i++) image.data[i * 4 + 3] = Math.round(settled[i]! * 255);
    maskCtx.putImageData(image, 0, 0);
    if (showMask) {
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(video, 0, 0, w, h);
      ctx.globalCompositeOperation = "destination-in";
      ctx.drawImage(maskCanvas, 0, 0, w, h);
      ctx.globalCompositeOperation = "destination-over";
      ctx.fillStyle = "#f0f";
      ctx.fillRect(0, 0, w, h);
      ctx.globalCompositeOperation = "source-over";
      const lines = [
        `parts ${partsState}${partsMs === null ? "" : `, ${Math.round(partsMs)}ms a run`}${field ? ", field in use" : ", no field"}`,
        `selfie ${Math.round((tiling ? segMs : wholeMs) ?? 0)}ms a frame, peak ${peak.toFixed(2)}, cutoff ${low.toFixed(2)} to ${high.toFixed(2)}`,
      ];
      const size = Math.max(14, Math.round(h / 32));
      ctx.font = `${size}px system-ui, sans-serif`;
      ctx.textBaseline = "top";
      lines.forEach((line, i) => {
        const y = size * 0.5 + i * size * 1.3;
        ctx.fillStyle = "rgba(0,0,0,0.7)";
        ctx.fillRect(size * 0.4, y - size * 0.15, ctx.measureText(line).width + size * 0.4, size * 1.25);
        ctx.fillStyle = "#fff";
        ctx.fillText(line, size * 0.6, y);
      });
      return;
    }

    // The person: the frame, kept only where the mask says person.
    personCtx.globalCompositeOperation = "source-over";
    personCtx.clearRect(0, 0, w, h);
    personCtx.drawImage(video, 0, 0, w, h);
    personCtx.globalCompositeOperation = "destination-in";
    personCtx.imageSmoothingEnabled = true;
    personCtx.imageSmoothingQuality = "high";
    // A slight feather on the mask so the edge is not a hard cut: about a
    // pixel at the mask's size, which the stretch to the frame widens.
    let edge: HTMLCanvasElement = maskCanvas;
    if (hasFilter) {
      featherCtx.clearRect(0, 0, mw, mh);
      featherCtx.filter = "blur(1px)";
      featherCtx.drawImage(maskCanvas, 0, 0);
      featherCtx.filter = "none";
      edge = featherCanvas;
    }
    personCtx.drawImage(edge, 0, 0, w, h);

    // The background without the person: the frame, cut where the mask says
    // person, then blurred.
    const bw = bg.width;
    const bh = bg.height;
    bgCtx.globalCompositeOperation = "source-over";
    bgCtx.clearRect(0, 0, bw, bh);
    bgCtx.drawImage(video, 0, 0, bw, bh);
    bgCtx.globalCompositeOperation = "destination-out";
    bgCtx.imageSmoothingEnabled = true;
    bgCtx.drawImage(maskCanvas, 0, 0, bw, bh);
    bgCtx.globalCompositeOperation = "source-over";
    bgBlurCtx.clearRect(0, 0, bw, bh);
    if (hasFilter) {
      // The same blur as w / 70 on the full frame, scaled to this canvas.
      bgBlurCtx.filter = `blur(${Math.max(2, Math.round(bw / 70))}px)`;
      bgBlurCtx.drawImage(bg, 0, 0, bw, bh);
      bgBlurCtx.filter = "none";
    } else {
      // Safari draws no canvas filter: shrink and stretch back, which averages
      // premultiplied color the same way.
      bgSmall.width = Math.max(16, Math.round(w / 16));
      bgSmall.height = Math.max(9, Math.round(h / 16));
      bgSmallCtx.clearRect(0, 0, bgSmall.width, bgSmall.height);
      bgSmallCtx.drawImage(bg, 0, 0, bgSmall.width, bgSmall.height);
      bgBlurCtx.imageSmoothingEnabled = true;
      bgBlurCtx.imageSmoothingQuality = "high";
      bgBlurCtx.drawImage(bgSmall, 0, 0, bw, bh);
    }

    // A coarse fill under it, for wherever the person covers the whole
    // neighborhood of a pixel (only ever seen through the person's own edge).
    small.width = Math.max(16, Math.round(w / 32));
    small.height = Math.max(9, Math.round(h / 32));
    smallCtx.drawImage(video, 0, 0, small.width, small.height);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(small, 0, 0, w, h);
    for (let i = 0; i < BACKGROUND_LAYERS; i++) ctx.drawImage(bgBlur, 0, 0, w, h);
    ctx.drawImage(person, 0, 0, w, h);
  };

  const drawSafely = (): void => {
    try {
      draw();
    } catch {
      // A frame that fails to segment is skipped; the next one tries again.
    }
  };
  type FrameVideo = HTMLVideoElement & { requestVideoFrameCallback?: (cb: () => void) => number };
  const frameVideo = video as FrameVideo;
  const onFrame = (): void => {
    if (stopped) return;
    lastFrameAt = performance.now();
    drawSafely();
    frameVideo.requestVideoFrameCallback?.(onFrame);
  };
  let lastFrameAt = 0;
  if (typeof frameVideo.requestVideoFrameCallback === "function") frameVideo.requestVideoFrameCallback(onFrame);
  // A timer underneath: the whole loop where there is no frame callback, and a
  // net where frame callbacks stall (a detached video can stop getting them).
  const tick = (): void => {
    if (stopped) return;
    if (performance.now() - lastFrameAt > 150) drawSafely();
    timer = setTimeout(tick, 1000 / FALLBACK_FPS);
  };
  tick();

  return {
    track,
    get source() {
      return raw;
    },
    setSource(next: MediaStreamTrack) {
      attach(next);
      raw = next;
      lastTime = -1;
    },
    stop() {
      stopped = true;
      if (timer) clearTimeout(timer);
      stopKeeping();
      raw.removeEventListener("unmute", replay);
      video.pause();
      video.srcObject = null;
      track.stop();
    },
  };
}
